import React from 'react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { Leaf, Recycle, Droplets, Hand } from 'lucide-react';

const u = (id, w = 1800) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=85`;

const Sustainability = () => {
  return (
    <div>
      <PageHeader
        eyebrow="Sustainability"
        title="Longevity is our first responsibility."
        intro="We believe the most sustainable garment is the one worn for a decade. Everything we do \u2014 from the mills we choose to the way we finish a hem \u2014 is built to extend that life."
      />

      <section className="px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal variant="image" duration={1400}>
            <div className="aspect-[16/9] overflow-hidden bg-neutral-100">
              <img src={u('photo-1545194828-092f11731eaf')} alt="Sustainable fabrics" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-10">
          {METRICS.map((m, i) => (
            <Reveal key={m.label} delay={i * 120}>
              <div className="text-center md:text-left">
                <p className="font-display text-5xl font-medium tracking-tight">{m.value}</p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mt-3">{m.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-[#fafaf8]">
        <div className="max-w-[1400px] mx-auto">
          <Reveal variant="fade">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4 text-center">Our Commitments</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-center mb-16">
              Four pillars, traceable end to end.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="bg-white p-10 h-full flex gap-6">
                  <div className="w-12 h-12 rounded-full bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-medium mb-2">{p.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal variant="fade">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-6">The Road Ahead</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-8">
              Our 2028 goals.
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <ul className="space-y-4 text-left text-[15px] leading-relaxed text-neutral-600">
              <li className="flex gap-4"><span className="text-neutral-900">&bull;</span> 100% of wool sourced from RWS-certified farms.</li>
              <li className="flex gap-4"><span className="text-neutral-900">&bull;</span> Zero virgin polyester across all collections.</li>
              <li className="flex gap-4"><span className="text-neutral-900">&bull;</span> A complimentary in-house repair programme for every Andobr\u00e9e piece, for life.</li>
              <li className="flex gap-4"><span className="text-neutral-900">&bull;</span> Carbon-neutral shipping on every order worldwide.</li>
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const METRICS = [
  { value: '78%', label: 'Natural fibres' },
  { value: '92%', label: 'EU-made' },
  { value: '0', label: 'Airfreight, always' },
  { value: '12', label: 'Mill partners' },
];

const PILLARS = [
  { icon: Leaf, title: 'Responsible Materials', body: 'Certified natural fibres, regenerative wool, OEKO-TEX dyes. Every fibre is traceable to its source.' },
  { icon: Droplets, title: 'Low-Impact Dyeing', body: 'All our silk and cotton are dyed in closed-loop facilities that recover over 90% of their water.' },
  { icon: Hand, title: 'Living Wages', body: 'Every partner is audited annually against living-wage benchmarks. We publish the list every December.' },
  { icon: Recycle, title: 'Repair & Resell', body: 'A free repair service for life, and a buy-back programme that keeps pieces in circulation, not landfill.' },
];

export default Sustainability;
